'use client';

import Link from 'next/link';
import type { PropsWithChildren } from 'react';
import { useRef, useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import {
  Prominence,
  Sizes,
  Text,
  Variants,
} from '@sola/ark-core/atoms/text/text.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { DateField } from '@sola/ark-core/molecules/date-field/date-field.tsx';
import { EmailField } from '@sola/ark-core/molecules/email-field/email-field.tsx';
import { Fieldset } from '@sola/ark-core/molecules/fieldset/fieldset.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { InputField } from '@sola/ark-core/molecules/input-field/input-field.tsx';
import { PostalCodeField } from '@sola/ark-core/molecules/postal-code-field/postal-code-field.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import type { SelectItemProps } from '@sola/ark-core/molecules/select/select.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { StateField } from '@sola/ark-core/molecules/state-field/state-field.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { generalConfig } from '@sola/config/lib/getters.ts';
import { StateEnum } from '@sola/schema/dotcom/shared/geo.ts';
import { BlockProps } from '@sola/schema/lib/schema.ts';
import type { CompassionSundayRegistrationFormData } from '@sola/schema/lib/skippy.ts';
import { submitCompassionSundayRegistration } from '@sola/skippy-sdk/requests.ts';
import { trackLeadFormSuccess } from '@sola/tracking/components/datalayer.tsx';

import { NameFields } from '../../../molecules/name-fields/name-fields.tsx';

const FormColumnProps = z.object({
  className: z.string().optional(),
});
type FormColumnProps = z.infer<typeof FormColumnProps> & PropsWithChildren;

const AddressFieldsProps = z.object({
  isChurchFields: z.boolean().optional(),
});
type AddressFieldsProps = z.infer<typeof AddressFieldsProps>;

const FormProps = BlockProps.extend({
  onSubmit: z
    .function({
      input: [z.custom<CompassionSundayRegistrationFormData>()],
      output: z.promise(z.boolean()),
    })
    .optional(),
  className: z.string().optional(),
});
type FormProps = z.infer<typeof FormProps>;

const FormState = z.enum(['submitting', 'error', 'success']).optional();
type FormState = z.infer<typeof FormState>;

const FormQueryState = z.object({
  isPastor: z.boolean().optional(),
  isChildHost: z.boolean().optional(),
  hasPermissionToHost: z.boolean().optional(),
});
type FormQueryState = z.infer<typeof FormQueryState>;

const radioValues = {
  yes: 'Yes',
  no: 'No',
};

const stateOptions = Object.entries(StateEnum).map(([abbr, name]) => ({
  text: name,
  value: abbr,
}));

const FormColumn: React.FC<FormColumnProps> = ({ className, children }) => (
  <div
    className={cn('grid sm:grid-cols-2 gap-comfy-sm', {
      [`${className}`]: !!className,
    })}
  >
    {children}
  </div>
);

const AddressFields: React.FC<AddressFieldsProps> = ({ isChurchFields }) => {
  const { t } = useTranslation(['core', 'forms', 'skippy']);
  const fieldGroupPrefix = isChurchFields ? 'church' : 'shipping';
  const autoCompletePrefix = isChurchFields ? '' : 'shipping ';

  const getTranslationKey = (key: string): string => {
    return `forms:addressFields.${key}`;
  };

  return (
    <>
      <InputField
        label={
          isChurchFields
            ? t('skippy:form.compassionSunday.churchAddressLabel')
            : t(getTranslationKey('addressLabel'))
        }
        name={`${fieldGroupPrefix}Address`}
        autoComplete={`${autoCompletePrefix}address-line1`}
        required
      />
      <FormColumn>
        <InputField
          label={t(getTranslationKey('cityLabel'))}
          name={`${fieldGroupPrefix}City`}
          autoComplete={`${autoCompletePrefix}address-level2`}
          required
          maxLength={generalConfig.leadForms.churchCityMaxLength}
        />
        <StateField
          label={t(getTranslationKey('stateLabel'))}
          name={`${fieldGroupPrefix}State`}
          autoComplete={`${autoCompletePrefix}address-level1`}
          spellCheck={false}
          placeholder={t(getTranslationKey('statePlaceholder'))}
          items={stateOptions}
          required
        />
        <PostalCodeField
          label={t(getTranslationKey('zipCodeLabel'))}
          name={`${fieldGroupPrefix}Postal`}
          autoComplete={`${autoCompletePrefix}postal-code`}
          required
        />
      </FormColumn>
    </>
  );
};

export const FormCompassionSundayRegistration: React.FC<FormProps> = ({
  className,
  onSubmit = submitCompassionSundayRegistration,
}) => {
  const { t } = useTranslation(['skippy']);
  const formRef = useRef<HTMLDivElement>(null);
  const [formState, setFormState] = useState<FormState>();
  const [{ isPastor, isChildHost, hasPermissionToHost }, setQueryState] =
    useState<FormQueryState>({});
  const showContactFields = isPastor || hasPermissionToHost;
  const showPermissionField = isPastor === false;
  const showHelpDetails = isPastor === false && hasPermissionToHost === false;
  const getTranslationKey = (key: string): string => {
    return `skippy:form.compassionSunday.${key}`;
  };

  /**
   * UPDATED 12/8/2025 - These are 2026 t-shirt size codes.
   */
  const tShirtSizes: SelectItemProps[] = [
    { value: '', text: t(getTranslationKey('tShirtSize.placeholder')) },
    { value: 'PM1499', text: t(getTranslationKey('tShirtSize.adultSm')) },
    { value: 'PM1500', text: t(getTranslationKey('tShirtSize.adultMd')) },
    { value: 'PM1501', text: t(getTranslationKey('tShirtSize.adultLg')) },
    { value: 'PM1502', text: t(getTranslationKey('tShirtSize.adultXL')) },
    { value: 'PM1503', text: t(getTranslationKey('tShirtSize.adultXXL')) },
    { value: 'PM1504', text: t(getTranslationKey('tShirtSize.adult3XL')) },
    { value: 'PM1505', text: t(getTranslationKey('tShirtSize.adult4XL')) },
    { value: 'PM1506', text: t(getTranslationKey('tShirtSize.youthSm')) },
    { value: 'PM1507', text: t(getTranslationKey('tShirtSize.youthMd')) },
    { value: 'PM1508', text: t(getTranslationKey('tShirtSize.youthLg')) },
    { value: 'PM1509', text: t(getTranslationKey('tShirtSize.youthXL')) },
    { value: 'none', text: t(getTranslationKey('tShirtSize.none')) },
  ];

  // TOOD: See about reusing the size options from Mike's leads form since they are the same
  const audienceSizes: SelectItemProps[] = [
    { value: '', text: t(getTranslationKey('audienceSize.placeholder')) },
    ...[
      '1-49',
      '50-99',
      '100-199',
      '200-299',
      '300-399',
      '400-499',
      '500-749',
      '750-999',
      '1,000',
      '2,000',
      '3,000',
      '4,000',
      '5,000',
      '6,000',
      '7,000',
      '8,000',
      '9,000',
      '10,000+',
    ].map((value) => ({ value, text: value })),
  ];

  const handleSubmit = async (data: FieldValues) => {
    setFormState('submitting');
    const registrationDate: CompassionSundayRegistrationFormData = {
      ...(data as CompassionSundayRegistrationFormData),
      isPastor: data.isPastor === radioValues.yes,
      withKids: data.withKids === radioValues.yes,
    };

    try {
      const success = await onSubmit(registrationDate);
      if (success) {
        trackLeadFormSuccess('CompassionSundayRegistration');
      }
      setFormState(success ? 'success' : 'error');
    } catch {
      setFormState('error');
    }
  };

  return (
    <section
      ref={formRef}
      className={cn(
        'CompassionSundayRegistration component component--no-header',
        {
          [`${className}`]: !!className,
        },
      )}
    >
      <div className="container">
        {formState === 'success' && (
          <Alert
            className="scroll-mt-roomy-md"
            variant="positive"
            message={t(getTranslationKey('successText'))}
            scrollIntoView
            scrollBehavior="auto"
          />
        )}
        {formState === 'error' && (
          <Alert
            className="mb-comfy-xl scroll-mt-roomy-md"
            variant="negative"
            message={t(getTranslationKey('errorText'))}
            scrollIntoView
          />
        )}
        {formState !== 'success' && (
          <Form
            className="CompassionSundayRegistration__form grid grid-col-1 md:items-end gap-comfy-lg md:gap-comfy-sm"
            onSubmit={handleSubmit}
            options={{ mode: 'onTouched' }}
            aria-label={t(getTranslationKey('registrationAriaLabel'))}
          >
            <Fieldset id="question-fields">
              <RadioGroup
                label={t(getTranslationKey('pastorQuestionLabel'))}
                name="isPastor"
                onChange={({ target }) =>
                  setQueryState((prevState) => ({
                    ...prevState,
                    isPastor: target.value === radioValues.yes,
                  }))
                }
                items={[
                  {
                    value: radioValues.yes,
                    text: t('core:actions.yes'),
                  },
                  {
                    value: radioValues.no,
                    text: t('core:actions.no'),
                  },
                ]}
              />
              <RadioGroup
                label={t(getTranslationKey('childQuestionLabel'))}
                name="withKids"
                onChange={({ target }) =>
                  setQueryState((prevState) => ({
                    ...prevState,
                    isChildHost: target.value === radioValues.yes,
                  }))
                }
                items={[
                  {
                    value: radioValues.yes,
                    text: t('core:actions.yes'),
                  },
                  {
                    value: radioValues.no,
                    text: t('core:actions.no'),
                  },
                ]}
              />
              <RadioGroup
                label={t(getTranslationKey('permissionLabel'))}
                fieldClasses={showPermissionField ? '' : 'hidden'}
                name="permission"
                onChange={({ target }) =>
                  setQueryState((prevState) => ({
                    ...prevState,
                    hasPermissionToHost: target.value === radioValues.yes,
                  }))
                }
                items={[
                  {
                    value: radioValues.yes,
                    text: t('core:actions.yes'),
                  },
                  {
                    value: radioValues.no,
                    text: t(getTranslationKey('permissionAnswerNo')),
                  },
                ]}
              />
            </Fieldset>
            <div className={showContactFields ? '' : 'hidden'}>
              <Fieldset
                id="contact-fields"
                header={t(getTranslationKey('contactHeader'))}
              >
                <FormColumn>
                  <NameFields />
                </FormColumn>
                <EmailField
                  label={t('forms:contactFields.emailLabel')}
                  name="email"
                  required
                />
              </Fieldset>
              <Fieldset
                id="shipping-fields"
                header={t(getTranslationKey('shippingHeader'))}
              >
                <AddressFields />
              </Fieldset>
              <Fieldset
                id="t-shirt-fields"
                header={t(getTranslationKey('tShirtHeader'))}
              >
                <FormColumn>
                  <Select
                    label={t(getTranslationKey('tShirtSizeLabel'))}
                    name="tShirtSize"
                    items={tShirtSizes}
                    required
                  />
                </FormColumn>
                <FormColumn className={isChildHost ? 'hidden' : ''}>
                  <Select
                    label={t(getTranslationKey('tShirtSizeCoHostLabel'))}
                    name="tShirtSizeCoHost"
                    items={tShirtSizes}
                    required={!isChildHost}
                  />
                </FormColumn>
                <FormColumn className={isChildHost ? '' : 'hidden'}>
                  <Select
                    label={t(getTranslationKey('tShirtSizeKidsLabel'))}
                    name="tShirtSizeKids"
                    items={tShirtSizes}
                    required={isChildHost}
                  />
                </FormColumn>
              </Fieldset>
              <Fieldset
                id="event-fields"
                header={t(getTranslationKey('eventHeader'))}
              >
                <FormColumn>
                  <DateField
                    label={t(getTranslationKey('eventDateLabel'))}
                    name="eventDate"
                    // TODO: should these next two props come from our settings/CMS at some point?
                    minDaysOut={21}
                    shouldSetInitialDate={false}
                    required
                  />
                </FormColumn>
                <Text
                  variant={Variants.Label}
                  size={Sizes.Large}
                  prominence={Prominence.Subtle}
                  asChild
                >
                  <p>{t(getTranslationKey('churchInfoDisclaimer'))}</p>
                </Text>
                <InputField
                  label={t(getTranslationKey('churchNameLabel'))}
                  name="churchName"
                  autoComplete="organization"
                  required
                />
                <AddressFields isChurchFields />
                <FormColumn>
                  <Select
                    label={t(getTranslationKey('audienceSize.label'))}
                    name="audienceSize"
                    items={audienceSizes}
                    required
                  />
                </FormColumn>
                <FormColumn className="-mt-snug-xl">
                  <Text
                    variant={Variants.Label}
                    size={Sizes.Small}
                    prominence={Prominence.Subtle}
                    asChild
                  >
                    <em>{t(getTranslationKey('audienceSize.helpText'))}</em>
                  </Text>
                </FormColumn>
              </Fieldset>
              <Checkbox
                fieldClasses={cn('mt-comfy-md', { hidden: !isChildHost })}
                label={t(getTranslationKey('acknowledgeLabel'))}
                name="acknowledged"
                required={isChildHost}
              />
              <FormSubmit
                className="CompassionSundayRegistration__button mt-comfy-lg"
                isBusy={formState === 'submitting'}
                disableUntilFilled
              >
                {t(getTranslationKey('register'))}
              </FormSubmit>
            </div>

            {showHelpDetails && (
              <div className="mt-comfy-sm">
                <Alert
                  message={
                    <>
                      <Text size={Sizes.XSmall} variant={Variants.Heading}>
                        {t(getTranslationKey('helpInfoTitle'))}
                      </Text>
                      <br />
                      <br />
                      <Text size={Sizes.Small}>
                        <IntlBlock
                          i18nKey={getTranslationKey('helpInfoText')}
                          // @ts-expect-error: Link expects an href but it is defined in the translation files
                          // eslint-disable-next-line jsx-a11y-x/anchor-is-valid
                          components={[<Link key="help-link" />]}
                        />
                      </Text>
                    </>
                  }
                />
              </div>
            )}
          </Form>
        )}
      </div>
    </section>
  );
};
